import { tasksRequest } from "./client";

/** Item du feed GET /tasks/me (Go meFeedItem). source discrimine l'origine :
 * "native" = tâche TM ; autres valeurs = miroir répliqué (PM/CRM) — exclu de
 * Chronos par l'arbitrage du 02/07 (filtre côté front, décision backend en attente). */
export interface MeFeedItem {
  source: string;
  id: string;
  tenant_id: string;
  title: string;
  status: string;
  priority?: string;
  due_date?: string;
  context_label?: string;
  source_url?: string;
  sort_at: string;
}

export interface TmTask {
  id: string;
  tenant_id: string;
  list_id?: string;
  parent_task_id?: string;
  title: string;
  description?: string;
  status:
    "todo" | "in_progress" | "blocked" | "done" | "cancelled" | "archived";
  priority: "low" | "medium" | "high" | "urgent";
  visibility: string;
  team_refs: string[];
  due_date?: string;
  reminder_at?: string;
  completed_at?: string;
  tags: string[];
  position: number;
  created_by_user_ref: string;
  created_at?: string;
  updated_at?: string;
}

export interface TmComment {
  id: string;
  task_id?: string;
  body: string;
  author_actor_type?: string;
  author_actor_ref?: string;
  created_at?: string;
}

export interface TmList<T> {
  items: T[];
  next_cursor?: string;
}

export interface CreateTmTaskInput {
  title: string;
  description?: string;
  priority?: TmTask["priority"];
  due_date?: string;
  tags?: string[];
  list_id?: string;
  /** Portée de lecture (Go createTaskBody.Visibility). Omis → "private" (défaut
   * domain.NewTask). "tenant" rend la tâche lisible par les managers/admins du
   * tenant ; l'écriture reste gardée par driver + ACL créateur. */
  visibility?: "private" | "team" | "tenant";
  /** Assignations RACI posées à la création (Go createTaskBody.Assignments) —
   * évite le POST /{id}/assignments en second appel non transactionnel. */
  assignments?: { actor_type: string; actor_ref: string; role: string }[];
}

export interface UpdateTmTaskInput {
  title?: string;
  description?: string;
  status?: TmTask["status"];
  priority?: TmTask["priority"];
  due_date?: string;
}

export function getMyFeed(cursor?: string): Promise<TmList<MeFeedItem>> {
  // limit = max backend (200) ; useMyTasks draine next_cursor via fetchAllPages.
  return tasksRequest({
    method: "GET",
    url: "/me",
    params: { limit: 200, ...(cursor ? { cursor } : {}) },
  });
}

export function listTmTasks(): Promise<TmList<TmTask>> {
  return tasksRequest({ method: "GET", url: "" });
}

export function getTmTask(id: string): Promise<TmTask> {
  return tasksRequest({ method: "GET", url: `/${id}` });
}

export function createTmTask(body: CreateTmTaskInput): Promise<TmTask> {
  return tasksRequest({ method: "POST", url: "", data: body });
}

export function updateTmTask(
  id: string,
  body: UpdateTmTaskInput,
): Promise<TmTask> {
  return tasksRequest({ method: "PATCH", url: `/${id}`, data: body });
}

export function completeTmTask(id: string): Promise<TmTask> {
  return tasksRequest({ method: "POST", url: `/${id}/complete`, data: {} });
}

export function listTmComments(taskId: string): Promise<TmList<TmComment>> {
  return tasksRequest({ method: "GET", url: `/${taskId}/comments` });
}

export function addTmComment(taskId: string, body: string): Promise<TmComment> {
  return tasksRequest({
    method: "POST",
    url: `/${taskId}/comments`,
    data: { body },
  });
}

/** Ligne task_assignments (colonnes REST assignments.go:19). actor_ref = TEXT
 * opaque (worker_url ou username) — matcher via l'ensemble des refs du user. */
export interface TmAssignment {
  id: string;
  tenant_id: string;
  task_id: string;
  actor_type: string;
  actor_ref: string;
  role: "driver" | "contributor" | "approver" | "informed" | "consulted";
  active: boolean;
  created_at: string;
}

export function listTmAssignments(
  taskId: string,
): Promise<TmList<TmAssignment>> {
  return tasksRequest({ method: "GET", url: `/${taskId}/assignments` });
}

/** Assignation RACI (actor_type "user", role driver|contributor|approver|informed|consulted). */
export function addTmAssignment(
  taskId: string,
  body: { actor_type: string; actor_ref: string; role: string },
): Promise<unknown> {
  return tasksRequest({
    method: "POST",
    url: `/${taskId}/assignments`,
    data: body,
  });
}
